import { File as FileIcon, X } from 'lucide-react'
import { useRef } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { DRAG_THRESHOLD } from '../consts'
import type { CanvasFileItem, CardPosition } from '../types'
import { formatBytes } from '../utils'

type DragState = {
  pointerId: number
  startClientX: number
  startClientY: number
  originX: number
  originY: number
  moved: boolean
}

export const CanvasFileCard = ({
  file,
  scale,
  selected,
  onSelect,
  onMove,
  onMoveEnd,
  onDelete,
  onContextMenu,
}: {
  file: CanvasFileItem
  scale: number
  selected: boolean
  onSelect: (opts?: { additive?: boolean }) => void
  onMove?: (position: CardPosition) => void
  onMoveEnd?: () => void
  onDelete?: () => void
  onContextMenu?: (e: React.MouseEvent) => void
}) => {
  const { t } = useLocalization()
  const dragState = useRef<DragState | null>(null)

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.stopPropagation()
    onSelect({ additive: e.shiftKey || e.metaKey || e.ctrlKey })
    if ((e.target as HTMLElement).closest('button')) return
    if (!onMove) return
    dragState.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      originX: file.x,
      originY: file.y,
      moved: false,
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // Pointer may already be released; drag still works via bubbling events.
    }
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current
    if (!drag || drag.pointerId !== e.pointerId) return
    const dxClient = e.clientX - drag.startClientX
    const dyClient = e.clientY - drag.startClientY
    if (!drag.moved && Math.hypot(dxClient, dyClient) > DRAG_THRESHOLD) {
      drag.moved = true
    }
    if (drag.moved && onMove) {
      onMove({
        x: drag.originX + dxClient / scale,
        y: drag.originY + dyClient / scale,
      })
    }
  }

  const endDrag = () => {
    if (dragState.current) {
      dragState.current = null
      onMoveEnd?.()
    }
  }

  const isImage = Boolean(
    file.url &&
      (file.mimeType.startsWith('image/') || file.url.startsWith('data:image/'))
  )

  return (
    <div
      className={cn(
        'absolute left-0 top-0 select-none rounded-xl',
        'cursor-grab active:cursor-grabbing',
        selected
          ? 'shadow-xl ring-2 ring-blue-500 ring-offset-2'
          : 'hover:shadow-xl'
      )}
      style={{ transform: `translate(${file.x}px, ${file.y}px)` }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onContextMenu={onContextMenu}
      onDragStart={(e) => e.preventDefault()}
    >
      {selected && onDelete ? (
        <button
          type="button"
          className="absolute -right-2.5 -top-2.5 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-white bg-gray-900 text-white shadow-md hover:bg-gray-700"
          onClick={onDelete}
          aria-label={t('Remove from canvas', 'Ta bort från canvas')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}

      {isImage ? (
        <img
          src={file.url}
          alt={file.name}
          className="block max-h-72 max-w-80 rounded-xl border border-gray-200 bg-white object-contain shadow-lg"
          draggable={false}
        />
      ) : (
        <Row className="w-56 items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
          <Row className="h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100">
            <FileIcon className="h-5 w-5 text-gray-500" />
          </Row>
          <Column className="min-w-0 gap-0.5">
            {file.url ? (
              <a
                href={file.url}
                target="_blank"
                rel="noreferrer"
                className="truncate text-xs font-medium text-gray-900 underline-offset-2 hover:underline"
                onPointerDown={(e) => e.stopPropagation()}
              >
                {file.name}
              </a>
            ) : (
              <p className="truncate text-xs font-medium text-gray-900">
                {file.name}
              </p>
            )}
            <p className="text-[11px] text-gray-500">
              {formatBytes(file.size)}
            </p>
          </Column>
        </Row>
      )}
    </div>
  )
}
